<template>
  <v-dialog
    v-model="dialogModel"
    max-width="800"
  >
    <v-card
      v-if="request"
      prepend-icon="mdi-file-document-outline"
      :title="`Requisição: ${request.code}`"
    >
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-information-outline"></v-icon>
              </template>
              <v-list-item-title>Descrição</v-list-item-title>
              <v-list-item-subtitle>{{ request.description }}</v-list-item-subtitle>
            </v-list-item>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-office-building"></v-icon>
              </template>
              <v-list-item-title>Setor</v-list-item-title>
              <v-list-item-subtitle>{{ getSectorName(request.sectorId) }}</v-list-item-subtitle>
            </v-list-item>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-calendar"></v-icon>
              </template>
              <v-list-item-title>Data da Requisição</v-list-item-title>
              <v-list-item-subtitle>{{ request.requestDate }}</v-list-item-subtitle>
            </v-list-item>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-account"></v-icon>
              </template>
              <v-list-item-title>Solicitante</v-list-item-title>
              <v-list-item-subtitle>{{ getUserName(request.userId) }}</v-list-item-subtitle>
            </v-list-item>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon icon="mdi-tag"></v-icon>
              </template>
              <v-list-item-title>Status</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  :color="getStatusColor(request.status)"
                  size="small"
                >
                  {{ getStatusText(request.status) }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item>
          </v-col>
          
          <v-col cols="12">
            <v-list-item v-if="request.note">
              <template v-slot:prepend>
                <v-icon icon="mdi-note-text"></v-icon>
              </template>
              <v-list-item-title>Observações</v-list-item-title>
              <v-list-item-subtitle>{{ request.note }}</v-list-item-subtitle>
            </v-list-item>
          </v-col>
        </v-row>
        
        <v-divider class="my-4"></v-divider>
        
        <div v-if="request.status !== REQUEST_STATUS.PENDING.id">
          <h3 class="text-h6 mb-2">Opções de Compra</h3>
          
          <div v-if="requestOptions && requestOptions.length > 0">
            <v-expansion-panels>
              <v-expansion-panel
                v-for="option in requestOptions"
                :key="option.id"
              >
                <v-expansion-panel-title>
                  {{ option.supplier }} - R$ {{ option.price.toFixed(2) }} ({{ option.unitType }})
                  <template 
                    v-if="request.status === REQUEST_STATUS.WAITING_APPROVAL.id && isApproverUser"
                    v-slot:actions
                  >
                    <v-btn
                      icon="mdi-check"
                      variant="text"
                      size="small"
                      color="success"
                      class="mr-2"
                      @click.stop="handleApproveOption(option)"
                    ></v-btn>
                  </template>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Fornecedor</v-list-item-title>
                        <v-list-item-subtitle>{{ option.supplier }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Preço Unitário</v-list-item-title>
                        <v-list-item-subtitle>R$ {{ option.price.toFixed(2) }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Quantidade</v-list-item-title>
                        <v-list-item-subtitle>{{ option.quantity }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-list-item>
                        <v-list-item-title>Unidade</v-list-item-title>
                        <v-list-item-subtitle>{{ option.unitType }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="6" md="6">
                      <v-list-item>
                        <v-list-item-title>Condição de Pagamento</v-list-item-title>
                        <v-list-item-subtitle>{{ option.paymentTerms }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>

                    <v-col cols="6" md="6">
                      <v-list-item>
                        <v-list-item-title>Previsão de Entrega</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedEta(option.eta) }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12">
                      <v-list-item v-if="option.observations">
                        <v-list-item-title>Observações</v-list-item-title>
                        <v-list-item-subtitle>{{ option.note }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                    
                    <v-col cols="12">
                      <v-list-item>
                        <v-list-item-title>Total</v-list-item-title>
                        <v-list-item-subtitle class="text-subtitle-1 font-weight-bold">
                          {{ formattedTotal(option) }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <v-alert
            v-else
            type="info"
            variant="tonal"
            class="mt-2"
          >
            Nenhuma opção de compra cadastrada.
          </v-alert>
          
          <v-divider class="my-4"></v-divider>
        </div>
        
        <v-expansion-panels variant="accordion" class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <h3 class="text-h6">Histórico</h3>
            </v-expansion-panel-title>
            
            <v-expansion-panel-text>
              <v-timeline density="compact" align="start">
                <v-timeline-item
                  v-for="(log, index) in activityLogs"
                  :key="index"
                  dot-color="primary"
                  size="small"
                >
                  <div>
                    <div class="text-subtitle-2">{{ formatDate(log.createdAt) }}</div>
                    <div class="d-flex align-center">
                      <v-icon size="small" class="me-1">mdi-account</v-icon>
                      <span class="text-caption font-weight-bold">{{ getUserName(log.userId) }}</span>
                    </div>
                    <div class="text-body-2 mt-1">{{ log.description }}</div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        
        <v-btn
          text="Fechar"
          variant="plain"
          @click="close"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { REQUEST_STATUS } from '@/helpers/constants/app.constants';

export default {
  name: 'RequestDetailsDialog',
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },

    request: {
      type: Object,
      default: null
    },

    optionId: {
      type: String,
      default: null
    }
  },

  data: () => ({
    REQUEST_STATUS
  }),
  
  computed: {
    ...mapState('general', ['generalList']),
    ...mapState('user', ['allUsers']),
    ...mapState('activityLogs', ['activityLogs']),

    ...mapGetters('general', ['sectors', 'suppliers', 'paymentMethods', 'unitTypes']),
    ...mapGetters('user', ['isApproverUser']),
    
    dialogModel: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    
    requestOptions() {
      return this.request.requestOptions?.map((option) => {
        return {
          ...option,
          supplier: this.suppliers.find((supplier) => supplier.id === option.supplierId)?.description,
          paymentTerms: this.paymentMethods.find((paymentMethod) => paymentMethod.id === option.paymentMethodId)?.description,
          unitType: this.unitTypes.find((unitType) => unitType.id === option.unitId)?.description,
        }
      });
    },
  },

  watch: {
    request: {
      deep: true,
      handler(newVal) {
        if (newVal?.id) {
          this.fetchActivityLogs(newVal.id);
        }
      }
    }
  },
  
  methods: {
    ...mapActions('request', ['approveOption']),
    ...mapActions('activityLogs', ['fetchActivityLogs']),

    formattedEta(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },

    getSectorName(sectorId) {
      const sector = this.sectors?.find(sector => sector.id === sectorId);
      return sector ? sector.description : 'Desconhecido';
    },
    
    getUserName(userId) {
      const user = this.allUsers.find((user) => user.id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Usuário não encontrado';
    },

    formattedTotal(option) {
      const total = (option.price || 0) * (option.quantity || 0);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(total);
    },
    
    getStatusText(status) {
      return REQUEST_STATUS[status].label;
    },
    
    getStatusColor(status) {
      return REQUEST_STATUS[status].color;
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },

    async handleApproveOption(option) {
      const { success } = await this.approveOption({ requestId: this.request.id, optionId: option.id });

      if(success) {
        this.$emit('snackbar', {
          text: 'Requisição aprovada com sucesso',
          color: 'success'
        });
      } else {
        this.$emit('snackbar', {
          text: 'Erro ao aprovar requisição',
          color: 'error'
        });
      }

      this.dialogModel = false;
    },
    
    close() {
      this.dialogModel = false;
      this.$emit('close');
    }
  }
}
</script> 